/**
 * מאזן — reading the pension clearing-house (מסלקה) report, IN THE BROWSER.
 *
 * A port of the partner's prototype parsers (nekudat-hakera.html). Runs
 * client-side on purpose: the raw report (every policy, every deposit) never
 * leaves the customer's device — only the structured fields they review in the
 * wizard are sent to the server. pdf.js and JSZip are bundled npm deps (no CDN)
 * and are loaded lazily, only when a file is actually dropped.
 *
 * Supported: the "Mimshak" uniform XML (מבנה אחיד), a ZIP of those, and the
 * "דוח ריכוז מוצרים פנסיונים" PDF (read by text position, then cross-checked
 * against the control totals on page 1 — a mismatch rejects the read). A PDF
 * that is not the clearing-house report is tried as a fund's / insurer's own
 * annual or quarterly report (periodic.ts); anything else is rejected as
 * unrelated.
 */
import { NOT_PENSION_REPORT, parsePeriodicReportPages } from './periodic'

export interface Deposit {
  valueDate: string
  salaryMonth: string
  amount: number
  source: 'XML' | 'PDF' | 'INSURER'
}

export interface MaslakaItem {
  body: string
  plan: string
  acct: string
  opened: string
  balance: number
  asOf: string
  active: boolean
  nikuim?: string
  deposits?: Deposit[]
  kind: string
  kitzba?: number
  gilPrisha?: number
  hishtalmut?: boolean
  /** provident fund / old pension fund — split by פסיקתא on the balance (no discount, no tax) */
  pesikta?: boolean
  /** first join date (ותק) — the transfer share and the study-fund clock run from it, not from the plan's opening */
  firstJoined?: string
  /** "מועד זכאות למשיכה בהטבת מס", when the report states it */
  liquidFrom?: string
  /** lump sum expected at retirement age WITHOUT further deposits */
  projected?: number
  why?: string
  /** dedupe key + sequence (two plans may share one policy number) */
  dk?: string
  seq?: number
  /** read from the fund's / insurer's own periodic report, not the clearing house */
  source?: 'insurer'
  /** e.g. 'דוח שנתי', 'דוח רבעוני' (insurer reports) */
  reportKind?: string
  /** the deposit list is complete from this date (the report's period start) — none missing */
  depositsFrom?: string
}

export interface MaslakaPerson {
  name: string
  id: string
  birth: string
  gender: '' | 'male' | 'female'
}

export interface MaslakaFileResult {
  file: string
  person: MaslakaPerson | null
  funds: MaslakaItem[]
  capital: MaslakaItem[]
  skipped: MaslakaItem[]
  error: string
  notes?: string[]
  /** read with OCR (no text layer) — the customer must double-check */
  ocr?: boolean
  /** the file is not the clearing-house original (re-printed, re-saved, scanned) */
  nonOriginal?: NonOriginal
  /** not read yet: waiting for the customer to choose "continue with this file" */
  pendingNonOriginal?: boolean
}

/** Why a PDF is not the original clearing-house file, in the customer's words. */
export interface NonOriginal {
  file: string
  /** what happened to the file, e.g. 'הודפס מחדש דרך "Microsoft Print to PDF"' */
  reason: string
  /** what "continue with this file" will do */
  detail: string
  /** the tool that wrote the file, as recorded in it (shown for transparency) */
  producer: string
  /** no text layer → will be read with OCR */
  noText: boolean
}

/**
 * Is this PDF the clearing-house original? Decided from the file's own
 * metadata (Producer / Creator) and whether it has any text. Only strong
 * signals count — a tool that is clearly not the clearing-house's — so a
 * genuine report is never flagged. Returns null for "looks original".
 */
export function detectNonOriginal(file: string, producer: string, creator: string, hasText: boolean): NonOriginal | null {
  const tool = [producer, creator].filter(Boolean).join(' / ')
  const p = tool.toLowerCase()
  const reason =
    /print to pdf/.test(p) ? 'הקובץ הודפס מחדש דרך "Microsoft Print to PDF" (הדפסה ל־PDF של Windows)'
    : /microsoft.*word|word for/.test(p) ? 'הקובץ נשמר מחדש מתוך Microsoft Word'
    : /excel/.test(p) ? 'הקובץ נשמר מחדש מתוך Microsoft Excel'
    : /powerpoint/.test(p) ? 'הקובץ נשמר מחדש מתוך Microsoft PowerPoint'
    : /libreoffice|openoffice/.test(p) ? 'הקובץ נשמר מחדש מתוך LibreOffice'
    : /google docs/.test(p) ? 'הקובץ נשמר מחדש מתוך Google Docs'
    : /quartz|mac os x/.test(p) ? 'הקובץ הודפס או נשמר מחדש במחשב Mac'
    : /scan|canon|epson|xerox|ricoh|kyocera|brother|lexmark|konica|sharp mx|hp digital sending/.test(p) ? 'הקובץ נסרק'
    : /ilovepdf|smallpdf|pdf24|sejda|nitro|foxit|pdfelement/.test(p) ? `הקובץ עבר עריכה או המרה בכלי PDF חיצוני (${tool})`
    : !hasText ? 'הקובץ נשמר מחדש או נסרק'
    : ''
  if (!reason) return null
  return {
    file,
    reason,
    producer: tool,
    noText: !hasText,
    detail: hasText
      ? 'הנתונים ייקראו מהקובץ כפי שהוא. ייתכן שמבנהו שונה מהמקור — יש לבדוק את הנתונים מול הדוח.'
      : 'אין בקובץ טקסט, ולכן הנתונים ייקראו בזיהוי תווים (OCR) — כדקה — ויש לבדוק אותם מול הדוח.',
  }
}

export interface MaslakaMerged {
  person: MaslakaPerson | null
  people: MaslakaPerson[]
  funds: MaslakaItem[]
  capital: MaslakaItem[]
  skipped: MaslakaItem[]
  errors: { file: string; msg: string }[]
  notes: string[]
  fileCount: number
  /** at least one file was read with OCR (no text layer) */
  ocr: boolean
  /** files that are not the clearing-house original */
  nonOriginal: NonOriginal[]
  /** some file awaits the customer's "continue with this file" */
  pendingNonOriginal: boolean
}

const ils = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 })

/* ─── Mimshak code tables ─────────────────────────────────────────────────── */
const SUG_MUTZAR: Record<string, string> = {
  '1': 'פוליסת ביטוח / ביטוח מנהלים',
  '2': 'קרן פנסיה',
  '3': 'קופת גמל',
  '4': 'קרן השתלמות',
  '5': 'קופת גמל מרכזית לפיצויים',
  '6': 'ביטוח חיים',
  '7': 'קופת ביטוח',
}
/* SUG-KEREN-PENSIA: 1 ותיקה · 2 חדשה מקיפה · 3 חדשה כללית (משלימה) */
const KEREN_PENSIA: Record<string, string> = { '1': 'ותיקה', '2': 'מקיפה', '3': 'משלימה' }
const SUPPORTED_KEREN = new Set(['2', '3'])
/* Capital assets: managers' insurance, provident funds, study funds, investment
   provident. Old pension funds and pure risk cover stay out. */
const CAPITAL_MUTZAR: Record<string, string> = {
  '1': 'ביטוח מנהלים',
  '3': 'קופת גמל',
  '4': 'קרן השתלמות',
  '5': 'קופת גמל מרכזית לפיצויים',
  '7': 'קופת ביטוח',
}
export const ONLY_PENSION = 'המחשבון מהוון קרן פנסיה מקיפה או משלימה בלבד'

const mTxt = (node: Element | undefined | null, tag: string): string => {
  if (!node) return ''
  const el = node.getElementsByTagName(tag)[0]
  return el && el.textContent ? el.textContent.trim() : ''
}
/** direct children only — the same tag repeats at different depths */
const mKids = (node: Element | undefined | null, tag: string): Element[] => {
  const out: Element[] = []
  if (node) for (const c of Array.from(node.children)) if (c.tagName === tag) out.push(c)
  return out
}
const mNum = (v: unknown): number => {
  const n = parseFloat(String(v || '').replace(/,/g, ''))
  return isFinite(n) ? n : 0
}
/** 20011129 → 2001-11-29 */
const mYmd = (v: unknown): string => {
  const s = String(v || '').trim()
  return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : ''
}
const mUnpad = (v: unknown): string => String(v || '').replace(/^0+/, '')

/* Deposit detail varies slightly between interface versions. Only rows with
   both a value date and recognised amount fields are read; otherwise the
   break-date adjustment stops rather than guessing. */
function xmlDeposits(hesh: Element): Deposit[] {
  const rows: Deposit[] = []
  const candidates = Array.from(hesh.getElementsByTagName('*')).filter((node) => {
    const tags = Array.from(node.children).map((c) => c.tagName.toUpperCase())
    return tags.some((t) => /TAARICH.*(ERECH|HAFKAD)/.test(t)) && tags.some((t) => /(SCHUM|SUM).*(HAFKAD|TAGMUL|PITZU|OVED|MAASIK)/.test(t))
  })
  for (const node of candidates) {
    const kids = Array.from(node.children)
    const dateChild = kids.find((c) => /TAARICH.*(ERECH|HAFKAD)/.test(c.tagName.toUpperCase()))
    const valueDate = mYmd(dateChild && dateChild.textContent)
    if (!valueDate) continue
    let amount = 0
    for (const c of kids) {
      const tag = c.tagName.toUpperCase()
      if (/(SCHUM|SUM).*(HAFKAD|TAGMUL|PITZU|OVED|MAASIK)/.test(tag) && !/(SACHAR|MASKORET|ACHSHAR)/.test(tag)) amount += mNum(c.textContent)
    }
    const salary = kids.find((c) => /(CHODESH|HODESH).*(SACHAR|MASKORET)/.test(c.tagName.toUpperCase()))
    if (amount > 0) rows.push({ valueDate, salaryMonth: String((salary && salary.textContent) || ''), amount: Math.round(amount * 100) / 100, source: 'XML' })
  }
  const seen = new Set<string>()
  return rows.filter((r) => {
    const k = `${r.valueDate}|${r.salaryMonth}|${r.amount}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/* Best effort: interface versions name these differently. Absent → the wizard
   asks the customer instead of guessing. */
function xmlLiquidFrom(hesh: Element): string | undefined {
  for (const el of Array.from(hesh.getElementsByTagName('*')))
    if (/NEZILUT|ZAKAUT/.test(el.tagName) && !el.children.length) {
      const d = mYmd(el.textContent)
      if (d) return d
    }
  return undefined
}
function xmlProjected(hesh: Element): number | undefined {
  let sum = 0
  for (const y of Array.from(hesh.getElementsByTagName('YitraLefiGilPrisha')))
    for (const el of Array.from(y.getElementsByTagName('*')))
      if (/^TOTAL-SCHUM-MTZBR-TZFUY/.test(el.tagName) && !el.children.length) sum += mNum(el.textContent)
  return sum > 0 ? Math.round(sum * 100) / 100 : undefined
}

/* Two plans can sit on one policy number and the PDF distinguishes them only by
   amount — so every row gets a sequence number. The merge identity is the
   bucket, not the label (XML and PDF label the same policy differently). */
function seqTag(list: MaslakaItem[], bucket: 'fund' | 'capital' | 'skip'): MaslakaItem[] {
  const seen = new Map<string, number>()
  for (const it of list) {
    it.dk = bucket === 'fund' ? `fund:${it.kind}` : bucket
    const k = `${it.acct}|${it.dk}`
    const n = seen.get(k) || 0
    it.seq = n
    seen.set(k, n + 1)
  }
  return list
}

export function readMaslakaXml(xmlText: string, fileName: string): MaslakaFileResult {
  const out: MaslakaFileResult = { file: fileName, person: null, funds: [], capital: [], skipped: [], error: '' }
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(xmlText, 'application/xml')
  } catch {
    out.error = 'הקובץ אינו XML תקין'
    return out
  }
  if (doc.getElementsByTagName('parsererror').length) {
    out.error = 'הקובץ אינו XML תקין'
    return out
  }

  const root = doc.documentElement
  if (!root || root.tagName !== 'Mimshak') {
    out.error = 'זה לא קובץ מסלקה במבנה אחיד'
    return out
  }

  for (const yatzran of Array.from(root.getElementsByTagName('YeshutYatzran'))) {
    const bodyName = mTxt(yatzran, 'SHEM-YATZRAN') || mTxt(yatzran, 'KOD-MEZAHE-YATZRAN')

    for (const mutzar of Array.from(yatzran.getElementsByTagName('Mutzar'))) {
      const netunei = mutzar.getElementsByTagName('NetuneiMutzar')[0]
      const sugMutzar = mTxt(netunei, 'SUG-MUTZAR')

      if (!out.person && netunei) {
        const l = netunei.getElementsByTagName('YeshutLakoach')[0]
        if (l) {
          const min = mTxt(l, 'MIN')
          out.person = {
            name: [mTxt(l, 'SHEM-PRATI'), mTxt(l, 'SHEM-MISHPACHA')].filter(Boolean).join(' '),
            id: mUnpad(mTxt(l, 'MISPAR-ZIHUY-LAKOACH')),
            birth: mYmd(mTxt(l, 'TAARICH-LEYDA')),
            gender: min === '2' ? 'female' : min === '1' ? 'male' : '',
          }
        }
      }

      for (const hesh of Array.from(mutzar.getElementsByTagName('HeshbonOPolisa'))) {
        const plan = mTxt(hesh, 'SHEM-TOCHNIT')
        const acct = mUnpad(mTxt(hesh, 'MISPAR-POLISA-O-HESHBON'))
        const keren = mTxt(hesh, 'SUG-KEREN-PENSIA')
        const label = plan || SUG_MUTZAR[sugMutzar] || 'מוצר'

        // Balance: every layer in every employer block (severance, employee, employer).
        let balance = 0
        let asOf = ''
        for (const block of Array.from(hesh.getElementsByTagName('BlockItrot'))) {
          for (const yit of mKids(block, 'Yitrot')) {
            asOf = asOf || mYmd(mTxt(yit, 'TAARICH-ERECH-TZVIROT'))
            for (const row of mKids(yit, 'PerutYitrot')) balance += mNum(mTxt(row, 'TOTAL-CHISACHON-MTZBR'))
          }
        }
        balance = Math.round(balance * 100) / 100

        // Expected monthly pension WITHOUT further deposits (KITZVAT-HODSHIT-TZFUYA).
        let kitzba = 0
        let gilPrisha = 0
        for (const y of Array.from(hesh.getElementsByTagName('YitraLefiGilPrisha'))) {
          gilPrisha = gilPrisha || mNum(mTxt(y, 'GIL-PRISHA'))
          for (const kupa of Array.from(y.getElementsByTagName('Kupa')))
            if (mTxt(kupa, 'SUG-KUPA') === '1') kitzba += mNum(mTxt(kupa, 'KITZVAT-HODSHIT-TZFUYA'))
        }

        const opened = mYmd(mTxt(hesh, 'TAARICH-HITZTARFUT-MUTZAR')) || mYmd(mTxt(hesh, 'TAARICH-HITZTARFUT-RISHON'))
        const common = {
          firstJoined: mYmd(mTxt(hesh, 'TAARICH-HITZTARFUT-RISHON')) || undefined,
          liquidFrom: xmlLiquidFrom(hesh),
          projected: xmlProjected(hesh),
          body: bodyName,
          plan: label,
          acct,
          opened,
          balance,
          asOf: asOf || mYmd(mTxt(hesh, 'TAARICH-NECHONUT')),
          active: mTxt(hesh, 'STATUS-POLISA-O-CHESHBON') === '1',
          deposits: xmlDeposits(hesh),
        }

        // An annuity product = any product with an expected monthly pension,
        // managers' insurance included — by the pension, not by active/inactive.
        if ((sugMutzar === '2' && SUPPORTED_KEREN.has(keren)) || (sugMutzar === '1' && kitzba > 0)) {
          out.funds.push({
            ...common,
            kind: sugMutzar === '1' ? 'ביטוח מנהלים' : `קרן פנסיה ${KEREN_PENSIA[keren] || ''}`.trim(),
            kitzba: Math.round(kitzba * 100) / 100,
            gilPrisha,
          })
        } else if (sugMutzar === '2' && keren === '1') {
          // old pension fund without a reported pension — פסיקתא on the balance
          out.capital.push({ ...common, kind: 'קרן פנסיה ותיקה', pesikta: true })
        } else if (CAPITAL_MUTZAR[sugMutzar]) {
          const gemel = (sugMutzar === '3' || sugMutzar === '5') && !/להשקעה/.test(label)
          out.capital.push({ ...common, kind: CAPITAL_MUTZAR[sugMutzar], hishtalmut: sugMutzar === '4', pesikta: gemel })
        } else {
          out.skipped.push({
            ...common,
            kind: sugMutzar === '2' ? (keren === '1' ? 'קרן פנסיה ותיקה' : 'קרן פנסיה — סוג לא מזוהה') : SUG_MUTZAR[sugMutzar] || 'מוצר לא מזוהה',
            why: ONLY_PENSION,
          })
        }
      }
    }
  }
  seqTag(out.funds, 'fund')
  seqTag(out.capital, 'capital')
  seqTag(out.skipped, 'skip')
  if (!out.funds.length && !out.capital.length && !out.skipped.length && !out.error) out.error = 'לא נמצאו חשבונות בקובץ'
  return out
}

/* ─── PDF: "דוח ריכוז מוצרים פנסיונים" ───────────────────────────────────── */
const DETAIL_HEADER = 'פירוט המוצרים על פי סוג המוצר'
export const OCR_NOTE =
  'הדוח נקרא בזיהוי תווים (OCR), כי קובץ ה־PDF נשמר מחדש ואין בו טקסט. חשוב לבדוק את הסכומים, התאריכים ומספרי הפוליסות מול הדוח לפני שממשיכים.'
export const OCR_FAILED =
  'קובץ ה־PDF נשמר מחדש ואין בו טקסט, וגם זיהוי התווים לא הצליח לקרוא ממנו דוח מסלקה. יש להעלות את הקובץ המקורי כפי שהורד מאתר המסלקה הפנסיונית (PDF, או קובץ ה־ZIP עם קובצי ה־XML), או להזין את הנתונים ידנית.'
export const NO_TEXT_PDF =
  'בקובץ ה־PDF אין טקסט קריא — כנראה נשמר מחדש דרך "הדפסה ל־PDF" או נסרק. יש להעלות את הקובץ המקורי כפי שהורד מאתר המסלקה הפנסיונית (PDF, או קובץ ה־ZIP עם קובצי ה־XML), או להזין את הנתונים ידנית.'
const PENSION_SECTION = 'קרנות פנסיה חדשות'
const PDF_ANCHORS = [
  'שם חברה מנהלת', 'סוג מוצר פנסיוני', 'מספר פוליסה', 'סטטוס',
  'תאריך פתיחת תכנית', 'תאריך נכונות נתונים', 'ח.פ חברה מנהלת',
  'מספר תיק ניכויים', 'תשואה מתחילת השנה', 'פנסיית נכות',
]

/** x = left edge; w = width when known (pdf.js text layer) — table cells are
 *  right-aligned (RTL), so columns line up on x + w, not on x. */
export interface PdfItem { x: number; y: number; str: string; w?: number }
interface PdfRow { y: number; lastY: number; items: PdfItem[]; text: string }

const norm = (s: unknown): string => String(s || '').replace(/[‎‏]/g, '').replace(/\s+/g, ' ').trim()
/** "1,810,519.06" → 1810519.06 */
const pdfNum = (s: unknown): number | null => {
  const m = norm(s).replace(/[^\d.,-]/g, '').replace(/,/g, '')
  const n = parseFloat(m)
  return isFinite(n) ? n : null
}
/** "29/11/2001" → 2001-11-29 */
const pdfDate = (s: unknown): string => {
  const m = norm(s).match(/(\d{2})\/(\d{2})\/(\d{4})/)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : ''
}

function pdfRows(items: PdfItem[]): PdfRow[] {
  const rows: PdfRow[] = []
  let cur: PdfRow | null = null
  for (const it of items.slice().sort((a, b) => b.y - a.y)) {
    if (!cur || cur.lastY - it.y > 10) {
      cur = { y: it.y, lastY: it.y, items: [it], text: '' }
      rows.push(cur)
    } else {
      cur.items.push(it)
      cur.lastY = it.y
    }
  }
  for (const r of rows) r.text = norm(r.items.slice().sort((a, b) => b.y - a.y || b.x - a.x).map((i) => i.str).join(' '))
  return rows
}

function pdfLabelEdge(rows: PdfRow[]): number {
  let maxValueX = 0
  for (const r of rows) {
    const single = r.items.filter((i) => PDF_ANCHORS.indexOf(norm(i.str)) !== -1)
    if (single.length !== 1) continue
    for (const it of r.items) if (it !== single[0]) maxValueX = Math.max(maxValueX, it.x)
  }
  return maxValueX ? maxValueX + 10 : 0
}

/**
 * OCR only: a label read one word short or with a letter off still matches its
 * expected label ("מוצר פנסיוני" → "סוג מוצר פנסיוני", "סה"כ חיסכון" →
 * "סה"כ חיסכון צבור") — but only when the fragment is most of the label and
 * unambiguous enough that no other label of the same table contains it.
 */
export function ocrLabelMatches(label: string, name: string): boolean {
  if (!label || !name) return false
  if (label.length >= name.length * 0.6 && (name.includes(label) || label.includes(name))) return true
  return label.length > 5 && name.length > 5 && Math.abs(label.length - name.length) <= 2 && levenshtein(label, name) <= 2
}

function levenshtein(a: string, b: string): number {
  const dp = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]
    dp[0] = i
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j]
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return dp[b.length]
}

function pdfSplit(row: PdfRow, edge: number) {
  const lab: PdfItem[] = []
  const val: PdfItem[] = []
  for (const it of row.items) (it.x >= edge ? lab : val).push(it)
  const label = norm(lab.sort((a, b) => b.y - a.y || b.x - a.x).map((i) => i.str).join(' '))
  const cells = val.filter((i) => !/^[₪*\s]*$|^או$/.test(norm(i.str))).sort((a, b) => b.x - a.x)
  return { label, cells }
}

/** where a cell lines up with its column: the right edge when the width is known */
const colPos = (it: PdfItem): number => (it.w != null ? it.x + it.w : it.x)
const hasDigit = (s: string) => /\d/.test(s)

/**
 * Cells → one value per column (anchors = the "מספר פוליסה" row's cells).
 * A text cell that wraps onto a second line ("פנסיה חדשה" / "מקיפה") is joined
 * in reading order; numbers and dates are never joined.
 */
function pdfByColumn(cells: PdfItem[], anchors: PdfItem[]): (string | null)[] {
  const ax = anchors.map(colPos)
  let gap = Infinity
  for (let k = 1; k < ax.length; k++) gap = Math.min(gap, Math.abs(ax[k - 1] - ax[k]))
  const guard = ax.length < 2 ? 120 : Math.max(20, Math.min(90, gap / 2 - 5))
  const out: (string | null)[] = ax.map(() => null)
  for (const c of cells.slice().sort((a, b) => b.y - a.y || b.x - a.x)) {
    let best = -1
    let dist = guard
    ax.forEach((a, k) => {
      const d = Math.abs(colPos(c) - a)
      if (d < dist) {
        dist = d
        best = k
      }
    })
    if (best === -1) continue
    const s = norm(c.str)
    const prev = out[best]
    if (prev == null) out[best] = s
    else if (!hasDigit(prev) && !hasDigit(s)) out[best] = prev + ' ' + s
  }
  return out
}

/**
 * The deposit amount on one row of a PDF deposit-detail page (partner V2, 2026-10-07).
 * Numbers to the LEFT of the salary-month column (or of the value date when the
 * row has no salary month), read right-to-left. The rightmost of them is
 * "שכר חודשי מדווח" — reported salary, NOT a deposit — so it is dropped; the
 * rest (employee / employer / severance components) are summed. V1 counted the
 * salary as a deposit.
 */
export function depositAmountFromRow(items: PdfItem[], dateItem: PdfItem, monthItem?: PdfItem): number {
  const values = items
    .filter((it) => it.x < (monthItem ? monthItem.x : dateItem.x) - 5 && /^-?[\d,]+(?:\.\d+)?$/.test(norm(it.str)))
    .sort((a, b) => b.x - a.x)
  return values.slice(1).reduce((sum, it) => sum + (pdfNum(it.str) || 0), 0)
}

async function loadPdfJsOnce() {
  // pdf.js 4 needs Promise.withResolvers (Chrome 119+, Safari 17.4+) — older
  // browsers get a tiny polyfill instead of failing to read every PDF.
  const P = Promise as unknown as { withResolvers?: () => unknown }
  if (typeof P.withResolvers !== 'function') {
    P.withResolvers = function withResolvers() {
      let resolve!: (v: unknown) => void
      let reject!: (e: unknown) => void
      const promise = new Promise((res, rej) => { resolve = res; reject = rej })
      return { promise, resolve, reject }
    }
  }
  const pdfjs = await import('pdfjs-dist')
  // Bundled worker (Vite emits it as an asset) — no CDN, no CSP exception.
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default
  return pdfjs
}

/** one retry — a chunk that failed to download (flaky network, a deploy
 *  swapping files mid-session) usually loads on the second try */
async function loadPdfJs() {
  try {
    return await loadPdfJsOnce()
  } catch (err) {
    console.error('[mazan] pdf.js failed to load, retrying:', err)
    await new Promise((r) => setTimeout(r, 800))
    return loadPdfJsOnce()
  }
}

export const PDF_READER_UNAVAILABLE =
  'לא הצלחנו לטעון את רכיב קריאת ה־PDF בדפדפן. רעננו את הדף (Ctrl+F5) ונסו שוב; אם זה חוזר — עדכנו את הדפדפן או העלו את קובצי ה־XML.'

export interface ReadOptions {
  /** progress messages for long work (OCR) — shown to the customer */
  onProgress?: (message: string) => void
  /** the customer chose to continue with a non-original file */
  acceptNonOriginal?: boolean
}

export async function readMaslakaPdf(buffer: ArrayBuffer, fileName: string, opts: ReadOptions = {}): Promise<MaslakaFileResult> {
  const out: MaslakaFileResult = { file: fileName, person: null, funds: [], capital: [], skipped: [], error: '' }
  let pdfjs: Awaited<ReturnType<typeof loadPdfJs>>
  try {
    pdfjs = await loadPdfJs()
  } catch (err) {
    console.error('[mazan] pdf.js could not load:', err)
    const why = err instanceof Error ? err.message : String(err)
    out.error = `${PDF_READER_UNAVAILABLE} (${why.slice(0, 120)})`
    return out
  }

  let doc
  try {
    doc = await pdfjs.getDocument({ data: new Uint8Array(buffer), isEvalSupported: false }).promise
  } catch {
    out.error = 'לא ניתן לפתוח את קובץ ה־PDF'
    return out
  }

  const pages: PdfItem[][] = []
  for (let n = 1; n <= doc.numPages; n++) {
    const tc = await (await doc.getPage(n)).getTextContent()
    pages.push(
      tc.items
        .filter((i): i is typeof i & { str: string; transform: number[]; width: number } => 'str' in i && !!i.str && !!i.str.trim())
        .map((i) => ({
          x: Math.round(i.transform[4]),
          y: Math.round(i.transform[5]),
          w: isFinite(i.width) && i.width > 0 ? Math.round(i.width) : undefined,
          str: i.str.trim(),
        }))
    )
  }

  // Not the clearing-house original (re-printed, re-saved, scanned)? Stop and
  // let the customer decide — upload the original, or continue with this file.
  const hasText = pages.some((p) => p.length > 0)
  const meta = await doc.getMetadata().catch(() => null)
  const info = (meta?.info || {}) as { Producer?: string; Creator?: string }
  const nonOriginal = detectNonOriginal(fileName, String(info.Producer || ''), String(info.Creator || ''), hasText)
  if (nonOriginal && !opts.acceptNonOriginal) {
    return { ...out, nonOriginal, pendingNonOriginal: true }
  }

  // No text layer at all — typically re-printed through Windows "Microsoft
  // Print to PDF" (every letter becomes a drawn outline) or scanned. It looks
  // right on screen; read it with OCR in the browser (the file still never
  // leaves the device), then run the very same parser on what was recognised.
  if (!hasText) {
    let ocrPages: PdfItem[][]
    try {
      const { ocrPdfPages } = await import('./ocr')
      ocrPages = await ocrPdfPages(doc, opts.onProgress)
    } catch (err) {
      // keep the reason visible for support (F12 → Console)
      console.error('[mazan] OCR could not run:', err)
      out.error = NO_TEXT_PDF
      return { ...out, nonOriginal: nonOriginal || undefined }
    }
    const res = parseMaslakaPdfPages(ocrPages, fileName, { ocr: true })
    res.ocr = true
    if (res.error) {
      res.error = OCR_FAILED
    } else {
      res.notes = [OCR_NOTE, ...(res.notes || [])]
    }
    return { ...res, nonOriginal: nonOriginal || undefined }
  }

  return { ...parseMaslakaPdfPages(pages, fileName), nonOriginal: nonOriginal || undefined }
}

/**
 * Parse the "דוח ריכוז מוצרים פנסיונים" from positioned text items (one array
 * per page, PDF units, y up) — from the PDF's own text layer, or from OCR.
 */
export function parseMaslakaPdfPages(pages: PdfItem[][], fileName: string, opts: { ocr?: boolean } = {}): MaslakaFileResult {
  const out: MaslakaFileResult = { file: fileName, person: null, funds: [], capital: [], skipped: [], error: '' }
  const allRows = pages.map(pdfRows)
  const flat = allRows.flat()

  if (!flat.some((r) => r.text.indexOf(DETAIL_HEADER) !== -1)) {
    // not the clearing-house report — maybe the fund's / insurer's own periodic report
    const periodic = parsePeriodicReportPages(pages, fileName)
    if (periodic) return periodic
    out.error = NOT_PENSION_REPORT
    return out
  }

  // ── the saver ──
  const head = flat.map((r) => r.text).join(' ')
  // names can be two words ("בן צבי") — read up to the next label
  const first = head.match(/שם פרטי:\s*(.{1,40}?)\s*(?=שם משפחה:|מס מזהה|$)/) || head.match(/שם פרטי:\s*([^\s]+)/)
  const last = head.match(/שם משפחה:\s*(.{1,40}?)\s*(?=מס מזהה|שם פרטי:|תאריך|סוג הבקשה|$)/) || head.match(/שם משפחה:\s*([^\s]+)/)
  const tz = head.match(/מס מזהה לקוח:?\s*(\d{5,9})/)
  out.person = {
    name: [first && first[1], last && last[1]].filter(Boolean).join(' '),
    id: tz ? tz[1] : '',
    birth: '', // the PDF carries no birth date
    gender: '', // nor gender
  }

  // ── control totals on page 1 ──
  let stated: number | null = null
  let statedAll: number | null = null
  const p1 = allRows[0] || []
  for (let i = 0; i < p1.length; i++) {
    const heads = p1[i].items.filter((it) => /קרנות פנסיה|חברות ביטוח|קופות גמל|קרנות השתלמות|גמל להשקעה/.test(norm(it.str)))
    if (!heads.length) continue
    for (let j = i + 1; j < Math.min(i + 4, p1.length); j++) {
      const nums = p1[j].items.filter((it) => pdfNum(it.str) != null && /\d/.test(it.str))
      if (nums.length < heads.length) continue
      let sum = 0
      let hit = 0
      for (const h of heads) {
        let best: PdfItem | null = null
        let dist = 40
        for (const it of nums) {
          const d = Math.abs(it.x - h.x)
          if (d < dist) {
            dist = d
            best = it
          }
        }
        if (!best) continue
        hit++
        sum += pdfNum(best.str) || 0
        if (norm(h.str).indexOf(PENSION_SECTION) === 0) stated = pdfNum(best.str)
      }
      if (hit === heads.length) {
        statedAll = sum
        break
      }
    }
    if (statedAll != null) break
  }

  // ── detail tables (read as one stream: a title may end one page, its table start the next) ──
  {
    const rows = flat.filter((r) => {
      const t = norm(r.text)
      return !/^עמוד \d+ מתוך/.test(t) && t.indexOf('שם פרטי:') !== 0 && t.indexOf('סוג הבקשה:') !== 0
    })
    const edge = pdfLabelEdge(rows)
    if (!edge) {
      out.error = 'מבנה הדוח לא זוהה — העלו את קובצי ה־XML'
      return out
    }

    for (let i = 0; i < rows.length; i++) {
      if (rows[i].text.indexOf(DETAIL_HEADER) === -1) continue
      i++
      if (i >= rows.length) break
      const section = norm(rows[i].text)
      if (!section || section.length > 60) continue
      const isPension = section.indexOf(PENSION_SECTION) === 0
      const risk = /ריסק|משכנתא|ביטוח חיים/.test(section)
      const capKind = risk
        ? ''
        : /השתלמות/.test(section)
          ? 'קרן השתלמות'
          : /גמל להשקעה/.test(section)
            ? 'גמל להשקעה'
            : /קופות גמל|קופת גמל/.test(section)
              ? 'קופת גמל'
              : /ביטוח מנהלים|חיסכון טהור|חברות ביטוח/.test(section)
                ? 'ביטוח מנהלים'
                : ''

      const body: PdfRow[] = []
      for (let j = i + 1; j < rows.length; j++) {
        const t = norm(rows[j].text)
        if (t.indexOf(DETAIL_HEADER) !== -1) break
        if (/^(ראה מקרא|\*|עמוד)/.test(t)) break
        body.push(rows[j])
      }
      if (!body.length) continue
      // OCR: one stray item elsewhere in the document can push the global
      // label/value boundary past the labels; use this table's own boundary.
      const secEdge = (opts.ocr && pdfLabelEdge(body)) || edge

      const bare = (s: string) => norm(s).replace(/[\s*]+$/, '')
      const get = (name: string) => {
        for (const r of body) {
          const s = pdfSplit(r, secEdge)
          if (bare(s.label) === bare(name) || (opts.ocr && ocrLabelMatches(bare(s.label), bare(name)))) return s
        }
        return null
      }
      const acctRow = get('מספר פוליסה') || get('שם חברה מנהלת')
      if (!acctRow || !acctRow.cells.length) {
        if (!isPension) out.skipped.push({ body: '', plan: section, acct: '', opened: '', balance: 0, asOf: '', active: false, kind: section, why: ONLY_PENSION })
        continue
      }
      const anchors = acctRow.cells
      const col = (name: string) => {
        const s = get(name)
        return s ? pdfByColumn(s.cells, anchors) : anchors.map(() => null)
      }

      const company = col('שם חברה מנהלת')
      const accts = pdfByColumn((get('מספר פוליסה') || { cells: [] as PdfItem[] }).cells, anchors)
      const kinds = col('סוג מוצר פנסיוני')
      const status = col('סטטוס')
      const bal = col('סה"כ חיסכון צבור')
      let kitz = col('קיצבה חודשית לגיל פרישה ללא הפקדות כספים נוספות')
      if (kitz.every((v) => v == null)) kitz = col('קיצבה חודשית לגיל פרישה ללא הפקדות כספים')
      const opened = col('תאריך פתיחת תכנית')
      const joined = col('תאריך הצטרפות לראשונה')
      const liquidFrom = col('מועד זכאות למשיכה בהטבת מס')
      // the label differs between the insurance and the provident-fund tables
      const projA = col('חיסכון צפוי לגיל פרישה ללא המשך הפקדות')
      const projB = col('חיסכון צפוי לגיל פרישה ללא הפקדות כספים נוספות')
      const asOf = col('תאריך נכונות נתונים')
      const nikuim = col('מספר תיק ניכויים')

      anchors.forEach((_, k) => {
        // OCR: the company name sits in white-on-colour header text, often
        // unreadable — leave it empty (flagged "חסר להשלמה") rather than guess.
        const body_ = company[k] || (opts.ocr ? '' : section)
        const acct = (accts[k] || '').replace(/^0+/, '')
        const kind = kinds[k] || ''
        const common = {
          body: body_,
          plan: kind || section,
          acct,
          opened: pdfDate(opened[k]),
          balance: pdfNum(bal[k]) || 0,
          asOf: pdfDate(asOf[k]),
          active: /^פעיל/.test(status[k] || ''),
          nikuim: (nikuim[k] || '').replace(/\D/g, ''),
          firstJoined: pdfDate(joined[k]) || undefined,
          // "ניתן למשיכה" = already redeemable
          liquidFrom: /ניתן למשיכה/.test(liquidFrom[k] || '') ? 'now' : pdfDate(liquidFrom[k]) || undefined,
          projected: pdfNum(projA[k]) ?? pdfNum(projB[k]) ?? undefined,
        }
        const k_ = pdfNum(kitz[k]) || 0
        if (!isPension && capKind === 'ביטוח מנהלים' && k_ > 0) {
          // managers' insurance with an expected pension — an annuity product
          out.funds.push({
            ...common,
            plan: [body_, 'ביטוח מנהלים'].filter(Boolean).join(' '),
            kind: 'ביטוח מנהלים',
            kitzba: k_,
            gilPrisha: 0,
          })
        } else if (/פנסיה ותיק/.test(section) || /ותיק/.test(kind)) {
          // old pension fund — פסיקתא on the balance (its pension isn't reported)
          out.capital.push({ ...common, plan: [body_, 'קרן פנסיה ותיקה'].filter(Boolean).join(' — '), kind: 'קרן פנסיה ותיקה', pesikta: true })
        } else if (isPension && /מקיפה|כללית|משלימה/.test(kind)) {
          const mashlima = /כללית|משלימה/.test(kind)
          out.funds.push({
            ...common,
            plan: [body_, mashlima ? 'משלימה' : 'מקיפה'].filter(Boolean).join(' '),
            kind: mashlima ? 'קרן פנסיה משלימה' : 'קרן פנסיה מקיפה',
            kitzba: pdfNum(kitz[k]) || 0,
            gilPrisha: 0,
          })
        } else if (capKind) {
          // the product type comes from the column's own field, not the section title
          const kindLabel = capKind === 'ביטוח מנהלים'
            ? (/חיסכון טהור/.test(kind) ? 'פוליסת חיסכון טהור' : /משולב/.test(kind) ? 'ביטוח חיים משולב חיסכון' : (kind || capKind))
            : capKind
          out.capital.push({
            ...common,
            plan: [body_, kindLabel].filter(Boolean).join(' — '),
            kind: kindLabel,
            hishtalmut: capKind === 'קרן השתלמות',
            pesikta: capKind === 'קופת גמל',
          })
        } else {
          out.skipped.push({ ...common, kind: section, why: ONLY_PENSION })
        }
      })
    }
  }

  // ── deposit detail ──
  // Each deposit table opens with its own header line: "סוג המוצר: … | שם חברה
  // מנהלת: … | מספר פוליסה: N". Rows belong to the account named in THEIR
  // block's header, and a block may continue onto the next page. (Searching the
  // whole page for an account number — the earlier approach — fails when one
  // page holds two tables, and when a pension policy number equals the saver's
  // ID, which is printed in every page header.) A block whose policy number
  // matches no single account is skipped, never guessed.
  const accounts = [...out.funds, ...out.capital]
  const firstWord = (s: string) => s.trim().split(/\s+/)[0] || ''
  let target: MaslakaItem | null = null
  for (const pageRows of allRows) {
    const pageText = pageRows.map((r) => r.text).join(' ')
    const isDepositPage = pageText.includes('תאריך ערך') || pageText.includes('פירוט הפקדות') || pageText.includes('פירוט ההפקדות')
    if (!isDepositPage) { target = null; continue }
    for (const row of pageRows) {
      if (/סוג המוצר/.test(row.text) && /מספר פוליסה/.test(row.text)) {
        const policy = (row.text.match(/מספר פוליסה:?\s*0*(\d{4,})/) || [])[1] || ''
        let hits = policy ? accounts.filter((f) => f.acct && f.acct === policy) : []
        if (!hits.length) {
          // the number didn't sit right after the label (OCR order) — accept any
          // number on this header line that is exactly one known account
          const nums = (row.text.match(/\d{5,}/g) || []).map((n) => n.replace(/^0+/, ''))
          const accts = new Set(accounts.filter((f) => f.acct && nums.includes(f.acct)).map((f) => f.acct))
          hits = accts.size === 1 ? accounts.filter((f) => accts.has(f.acct)) : []
        }
        if (!hits.length) {
          // still nothing (number unreadable): the header also names the product
          // type and the company — accept only a single account matching BOTH
          const t = row.text
          const typeOf = (f: MaslakaItem) =>
            out.funds.includes(f) ? /פנסיה/.test(t)
              : f.kind === 'קרן השתלמות' ? /השתלמות/.test(t)
              : f.kind === 'ביטוח מנהלים' ? /ביטוח/.test(t)
              : /גמל/.test(t) && !/השתלמות/.test(t)
          hits = accounts.filter((f) => f.body && typeOf(f) && t.includes(firstWord(f.body)))
        }
        if (hits.length > 1) {
          // two accounts share the number (a pension policy number is often the
          // saver's ID) — the header's company name decides, if it names just one
          const byCompany = hits.filter((f) => f.body && row.text.includes(firstWord(f.body)))
          if (byCompany.length === 1) hits = byCompany
        }
        target = hits.length === 1 ? hits[0] : null
        continue
      }
      if (!target) continue
      const dateItem = row.items.find((it) => /^\d{2}\/\d{2}\/\d{4}$/.test(norm(it.str)))
      if (!dateItem) continue
      const monthItem = row.items.find((it) => /^\d{2}\/\d{4}$/.test(norm(it.str)))
      const amount = depositAmountFromRow(row.items, dateItem, monthItem)
      if (amount > 0) {
        const deposits = target.deposits || (target.deposits = [])
        deposits.push({ valueDate: pdfDate(dateItem.str), salaryMonth: monthItem ? norm(monthItem.str) : '', amount: Math.round(amount * 100) / 100, source: 'PDF' })
      }
    }
  }

  // ── control: what was read must match the stated pension total ──
  if (out.funds.length && stated != null) {
    // the stated total is of the new pension funds only — managers' insurance moved to the annuity list isn't in it
    const sum = out.funds.filter((f) => f.kind !== 'ביטוח מנהלים').reduce((t, f) => t + f.balance, 0)
    if (Math.abs(sum - stated) > 2) {
      out.error = `הקריאה מה־PDF לא הסתדרה: נקראו ${ils.format(sum)} מול ${ils.format(stated)} שמופיעים בדוח. העלו את קובצי ה־XML.`
      out.funds = []
      out.capital = []
      out.skipped = []
      return out
    }
  }
  if (statedAll != null && (out.funds.length || out.capital.length)) {
    const all = [...out.funds, ...out.capital, ...out.skipped].reduce((t, f) => t + (f.balance || 0), 0)
    if (Math.abs(all - statedAll) > 2) {
      out.notes = [
        `סך הצבירות שנקראו מה־PDF הוא ${ils.format(all)}, ובדוח מופיע ${ils.format(statedAll)} — הפרש של ${ils.format(Math.abs(statedAll - all))}. הדוח מציג לעיתים שתי תוכניות על שורה אחת. לדיוק מלא העלו גם את קובצי ה־XML.`,
      ]
    }
  }
  seqTag(out.funds, 'fund')
  seqTag(out.capital, 'capital')
  seqTag(out.skipped, 'skip')
  if (!out.funds.length && !out.capital.length && !out.skipped.length) out.error = 'לא נמצאו מוצרים בדוח'
  return out
}

/** Merge every uploaded file into one picture of the saver. */
export function mergeMaslaka(results: MaslakaFileResult[]): MaslakaMerged {
  const m: MaslakaMerged = { person: null, people: [], funds: [], capital: [], skipped: [], errors: [], notes: [], fileCount: results.length, ocr: results.some((r) => r.ocr),
    nonOriginal: results.flatMap((r) => (r.nonOriginal ? [r.nonOriginal] : [])), pendingNonOriginal: results.some((r) => r.pendingNonOriginal) }
  for (const r of results) {
    if (r.pendingNonOriginal) continue // not read yet — the customer decides first
    if (r.error) {
      m.errors.push({ file: r.file, msg: r.error })
      continue
    }
    if (r.person && r.person.id) {
      if (!m.people.some((p) => p.id === r.person!.id)) m.people.push(r.person)
      // PDF has name + ת.ז but no birth/gender; XML has all — build one person from everything.
      if (!m.person) m.person = { ...r.person }
      else for (const k of ['name', 'id', 'birth', 'gender'] as const) if (!m.person[k] && r.person[k]) (m.person as unknown as Record<string, string>)[k] = r.person[k]
    }
    m.funds.push(...r.funds)
    m.capital.push(...(r.capital || []))
    if (r.notes) m.notes.push(...r.notes.filter((n) => n !== OCR_NOTE))
    m.skipped.push(...r.skipped)
  }

  const mergeBy = (list: MaslakaItem[]) => {
    const by = new Map<string, MaslakaItem>()
    for (const f of list) {
      const k = `${f.acct}|${f.dk}|${f.seq}`
      const prev = by.get(k) as unknown as Record<string, unknown> | undefined
      if (!prev) {
        by.set(k, { ...f })
        continue
      }
      for (const key of Object.keys(f)) {
        const v = (f as unknown as Record<string, unknown>)[key]
        if (key === 'deposits' && Array.isArray(v) && v.length) {
          prev.deposits = v
          continue
        }
        if (v !== '' && v !== 0 && v != null && !prev[key]) prev[key] = v
      }
    }
    return [...by.values()]
  }
  m.funds = mergeBy(m.funds)
  m.capital = mergeBy(m.capital)

  // A fund's own report is never merged into a clearing-house row (two reports,
  // two dates — mixing them would be wrong). If both describe the same fund,
  // say so, so it isn't counted twice.
  const firstWord = (s: string) => s.trim().split(/\s+/)[0] || ''
  for (const list of [m.funds, m.capital]) {
    for (const ins of list.filter((f) => f.source === 'insurer')) {
      const twin = list.find((o) => o !== ins && o.source !== 'insurer' && o.kind === ins.kind &&
        ((ins.acct && o.acct === ins.acct) || (!!ins.body && firstWord(o.body) === firstWord(ins.body))))
      if (twin)
        m.notes.push(`ייתכן ש"${ins.plan}" מופיעה פעמיים — גם בדוח המסלקה וגם בדוח מהקופה. אם זו אותה קופה, יש למחוק אחת מהשורות כדי שלא תיספר פעמיים.`)
    }
  }

  const taken = new Set([...m.funds, ...m.capital].map((f) => f.acct))
  const seenSkip = new Set<string>()
  m.skipped = m.skipped.filter((s) => {
    const k = `${s.acct}|${s.dk}|${s.seq}`
    if (seenSkip.has(k)) return false
    seenSkip.add(k)
    return !(s.acct && taken.has(s.acct) && /פנסיה|גמל|השתלמות/.test(s.kind))
  })
  return m
}

const MAX_FILE_BYTES = 25 * 1024 * 1024
const MAX_ZIP_ENTRIES = 200

/** Read what the user dropped / picked: XML files, a ZIP of them, or the PDF. */
export async function filesToResults(fileList: File[], opts: ReadOptions = {}): Promise<MaslakaFileResult[]> {
  const fail = (file: string, error: string): MaslakaFileResult => ({ file, person: null, error, funds: [], capital: [], skipped: [] })
  const results: MaslakaFileResult[] = []
  for (const file of fileList) {
    const lower = file.name.toLowerCase()
    if (file.size > MAX_FILE_BYTES) {
      results.push(fail(file.name, 'הקובץ גדול מדי (מעל 25MB)'))
    } else if (lower.endsWith('.zip')) {
      try {
        const { default: JSZip } = await import('jszip')
        const zip = await JSZip.loadAsync(file)
        const entries = Object.values(zip.files).filter((e) => !e.dir && e.name.toLowerCase().endsWith('.xml')).slice(0, MAX_ZIP_ENTRIES)
        if (!entries.length) results.push(fail(file.name, 'ה־ZIP אינו מכיל קובצי XML'))
        for (const e of entries) results.push(readMaslakaXml(await e.async('string'), e.name.split('/').pop() || e.name))
      } catch {
        results.push(fail(file.name, 'לא ניתן לפתוח את קובץ ה־ZIP'))
      }
    } else if (lower.endsWith('.xml')) {
      results.push(readMaslakaXml(await file.text(), file.name))
    } else if (lower.endsWith('.pdf')) {
      results.push(await readMaslakaPdf(await file.arrayBuffer(), file.name, opts))
    } else {
      results.push(fail(file.name, 'סוג קובץ שאינו נתמך — נדרש PDF, XML או ZIP מהמסלקה'))
    }
  }
  return results
}

/* ─── adjusting the report to the break date (מועד הקרע) ─────────────────── */
const parseDate = (v?: string | null): Date | null => (v ? new Date(v + 'T00:00:00Z') : null)
const dmy = (d: Date) => d.toISOString().slice(0, 10).split('-').reverse().join('/')

function addCalendarMonths(date: Date, count: number): Date {
  const d = new Date(date.getTime())
  const day = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + count)
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(day, last))
  return d
}

/** "03/2024", "3.24", "03-2024" → 2024-03-01 (UTC); anything else → null. */
export function salaryMonthStart(d: Pick<Deposit, 'salaryMonth'>): Date | null {
  const s = String(d.salaryMonth || '').trim()
  const m = s.match(/(\d{1,2})[/.\-](\d{2,4})/)
  if (!m) return null
  let y = parseInt(m[2], 10)
  if (y < 100) y += 2000
  return new Date(Date.UTC(y, parseInt(m[1], 10) - 1, 1))
}

export type AdjustmentStatus = 'ok' | 'pending' | 'blocked'

export interface AdjustedItem extends MaslakaItem {
  originalBalance: number
  originalKitzba?: number
  deducted: number
  adjustmentStatus: AdjustmentStatus
  adjustmentMessage: string
}

/**
 * Roll a reported balance back to the break date using ACTUAL deposits only:
 * report must cover the break date, be at most 6 months after it, and carry
 * full deposit detail — otherwise the flow blocks (business rule, AGENTS.md).
 */
export function adjustFundToBreak(f: MaslakaItem, breakDate: Date | null): AdjustedItem {
  const asOf = parseDate(f.asOf)
  const base: AdjustedItem = { ...f, originalBalance: f.balance, originalKitzba: f.kitzba, deducted: 0, adjustmentStatus: 'ok', adjustmentMessage: '' }
  if (!breakDate) return { ...base, adjustmentStatus: 'pending', adjustmentMessage: 'יש למלא את מועד הקרע כדי לבדוק את הדוח' }
  if (!asOf) return { ...base, adjustmentStatus: 'blocked', adjustmentMessage: 'תאריך נכונות הנתונים חסר; נדרש דוח תקופתי מחברת הביטוח' }
  // both dates in the message, so the customer sees how far apart they are
  const dates = `(הדוח נכון ל־${dmy(asOf)}, מועד הקרע ${dmy(breakDate)})`
  if (asOf < breakDate) return { ...base, adjustmentStatus: 'blocked', adjustmentMessage: `הדוח מוקדם ממועד הקרע ${dates}; נדרש דוח מעודכן המכסה את מועד הקרע` }
  if (asOf > addCalendarMonths(breakDate, 6))
    return { ...base, adjustmentStatus: 'blocked', adjustmentMessage: `הדוח רחוק ביותר משישה חודשים ממועד הקרע ${dates}; נדרש דוח תקופתי מחברת הביטוח` }
  if (asOf.getTime() === breakDate.getTime()) return { ...base, adjustmentMessage: 'הדוח נכון למועד הקרע — לא נדרש קיזוז' }
  const deposits = Array.isArray(f.deposits) ? f.deposits : []
  const dated = deposits.filter((d) => parseDate(d.valueDate))
  const earliestValue = dated.map((d) => parseDate(d.valueDate)!).sort((a, b) => a.getTime() - b.getTime())[0]
  const coverageStart = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() - 6, 1))
  // A fund's own periodic report lists EVERY deposit of its period (or states
  // there were none) — complete from the period start, even with no rows.
  const listedFrom = parseDate(f.depositsFrom)
  const covered = listedFrom ? listedFrom <= breakDate : !!dated.length && !!earliestValue && earliestValue <= coverageStart
  if (!covered) {
    return { ...base, adjustmentStatus: 'blocked', adjustmentMessage: 'אין בדוח פירוט הפקדות מלא המכסה את התקופה ממועד הקרע; נדרש דוח תקופתי מחברת הביטוח' }
  }
  // Partner V2 (2026-10-07): a deposit is "after the break" by its SALARY MONTH,
  // not its value date — the deposit for the break month's own salary belongs to
  // the shared period even when it was paid after it. No salary month → fall back
  // to the value date, as before.
  const breakMonthStart = new Date(Date.UTC(breakDate.getUTCFullYear(), breakDate.getUTCMonth(), 1))
  const after = dated.filter((d) => {
    const sm = salaryMonthStart(d)
    if (sm) return sm > breakMonthStart
    const dt = parseDate(d.valueDate)!
    return dt > breakDate && dt <= asOf
  })
  const deducted = Math.round(after.reduce((sum, d) => sum + d.amount, 0) * 100) / 100
  if (deducted > f.balance)
    return { ...base, adjustmentStatus: 'blocked', adjustmentMessage: 'סכום ההפקדות המאוחרות גדול מהיתרה המדווחת; נדרש דוח תקופתי מחברת הביטוח' }
  const balance = Math.round((f.balance - deducted) * 100) / 100
  const kitzba = f.balance > 0 && f.kitzba != null ? Math.round(((f.kitzba * balance) / f.balance) * 100) / 100 : f.kitzba
  return {
    ...base,
    deducted,
    balance,
    kitzba,
    adjustmentMessage: deducted > 0 ? `קוזזו הפקדות בפועל בסך ${ils.format(deducted)}` : 'לא נמצאו הפקדות לאחר מועד הקרע',
  }
}
